/**
 * RTK Query API for Dashboard operations
 *
 * This API replaces the deprecated `redux/reducers/dashboard`.
 * All dashboard operations should use these hooks instead of Redux actions.
 *
 * @example
 * // Fetching dashboard data
 * const { data, isLoading } = useGetDashboardQuery({
 *   startDate: new Date('2024-01-01'),
 *   endDate: new Date('2024-12-31'),
 *   parent: parentCompanyId
 * });
 *
 * @example
 * // With date range filtering
 * const { data } = useGetDashboardQuery({
 *   startDate: startOfMonth(new Date()),
 *   endDate: endOfMonth(new Date())
 * });
 */
import { getSearchParams } from "../../../utils/request";
import { api } from "../../api";

const dashboardApi = api.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Get dashboard statistics and data
     * @param {Object} params - Query parameters
     * @param {Date|string} params.startDate - Start date for date range filter
     * @param {Date|string} params.endDate - End date for date range filter
     * @param {string} params.parent - Parent company ID
     * @returns Dashboard data including jobs, invoices, stats, etc.
     */
    getDashboard: builder.query({
      query: (params = {}) => {
        const query = getSearchParams({ ...params });
        return `dashboard?${query}`;
      },
      transformResponse: (response) => response.data,
      providesTags: ["Dashboard"],
    }),

    /**
     * Get dashboard statistics for a specific time period
     * @param {Object} params - Query parameters
     * @param {string} params.period - Time period (day, week, month, year)
     * @param {string} params.parent - Parent company ID
     * @returns Statistics for the specified period
     */
    getDashboardStats: builder.query({
      query: ({ period = "month", ...rest }) => {
        const query = getSearchParams({ period, ...rest });
        return `dashboard/stats?${query}`;
      },
      transformResponse: (response) => response.data,
      providesTags: ["Dashboard"],
    }),

    /**
     * Get recent activity for the dashboard
     * @param {Object} params - Query parameters
     * @param {number} params.limit - Number of recent items to fetch
     * @param {string} params.parent - Parent company ID
     * @returns Recent activity items
     */
    getDashboardActivity: builder.query({
      query: ({ limit = 10, ...rest }) => {
        const query = getSearchParams({ limit, ...rest });
        return `dashboard/activity?${query}`;
      },
      transformResponse: (response) => response.data,
      providesTags: ["Dashboard"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetDashboardQuery,
  useLazyGetDashboardQuery,
  useGetDashboardStatsQuery,
  useGetDashboardActivityQuery,
} = dashboardApi;



